import {
  AUTOMATION_EVENT_OPTIONS,
  TRACKER_CATEGORIES,
  TRACKER_KINDS,
  TRACKER_STATUS,
  SUBMISSION_MODES,
  NO_MATCH_BEHAVIOURS,
  REVIEW_SCHEDULE_TYPES,
  createEmptyForm,
  createEmptyStatus,
  createDefaultPermissions,
  createDefaultGymTvViews,
  createDefaultCoachReview,
  createCoachReviewQuestion,
  buildDefaultPersonalInfoQuestions,
  normalizeFormQuestion,
  FORM_QUESTION_SOURCES,
  DEFAULT_STATUS_COLORS,
  ensureTrackerShape,
} from "./trackerStorage";
import {
  DEFAULT_TRACKER_QUESTIONS,
  getQuestions,
  mergeMissingDefaultQuestions,
} from "./questions/questionStorage";
import {
  createEmptyAutomation,
  createEmptyWorkflow,
  normalizeAutomation,
} from "./automations/automationStorage";

const STORAGE_KEY = "gymrite_trackers";
const DEMO_FLAG_KEY = "gymrite_journey_demo_v2";

const nowIso = () => new Date().toISOString();

const DEMO_COACHES = [
  {
    id: "coach_demo_1",
    first_name: "Sarah",
    last_name: "Murphy",
    email: "sarah.murphy@gymrite.demo",
  },
  {
    id: "coach_demo_2",
    first_name: "James",
    last_name: "O'Brien",
    email: "james.obrien@gymrite.demo",
  },
  {
    id: "coach_demo_3",
    first_name: "Aoife",
    last_name: "Walsh",
    email: "aoife.walsh@gymrite.demo",
  },
];

const demoCreateSetup = (overrides = {}) => ({
  durationWeeks: 12,
  customDurationDays: 21,
  journeyStartsFrom: "from_people_start",
  publishDate: null,
  checkInMode: "weekly",
  checkInWeekdays: [4],
  checkInMonthDay: 1,
  checkInEveryDays: 7,
  coachReviewMode: "every_n_weeks",
  coachReviewEveryWeeks: 4,
  ...overrides,
});

const libraryById = () => {
  const list = mergeMissingDefaultQuestions(getQuestions());
  return new Map(
    [...DEFAULT_TRACKER_QUESTIONS, ...list].map((q) => [q.id, q])
  );
};

const fq = (libMap, stableId, questionId, extras = {}) => {
  const lib = libMap.get(questionId) || {};
  return normalizeFormQuestion(
    {
      id: stableId,
      source: FORM_QUESTION_SOURCES.LIBRARY,
      questionId,
      label: lib.label || extras.label || questionId,
      fieldType: lib.fieldType || "short_text",
      mode: lib.mode || "general",
      measurable: lib.mode === "measurable",
      measureType: lib.measureType || "",
      units: lib.units || [],
      defaultUnit: lib.defaultUnit || "",
      measureDirection: lib.measureDirection || "descending",
      options: lib.options || [],
      required: extras.required !== false,
      searchable: true,
      filterable: true,
      helpText: extras.helpText || lib.helpText || "",
    },
    extras.order || 0
  );
};

const status = (id, name, color, extras = {}) =>
  createEmptyStatus(name, color, { id, ...extras });

const form = (id, overrides = {}) =>
  createEmptyForm({
    id,
    ...overrides,
  });

const scheduleId = (prefix, key) => `${prefix}_${key}`;

/**
 * Real GymRite Journey trackers from the development specification.
 * Questions/forms are specific to each journey purpose.
 */
export const buildDemoJourneyTrackers = () => {
  const personal = buildDefaultPersonalInfoQuestions();
  const lib = libraryById();
  const q = (stableId, questionId, extras = {}) =>
    fq(lib, stableId, questionId, extras);
  const ts = nowIso();

  const chasingBetter = ensureTrackerShape({
    id: "tracker_chasing_better",
    name: "Chasing Better Journey",
    description:
      "Ongoing transformation journey with 30 / 60 / 90 day reviews relative to sign-up.",
    visitorMessage:
      "Welcome to Chasing Better. Complete your baseline, then return for each review checkpoint.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.JOURNEY,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_people_start",
    questionPattern: "different_forms_in_order",
    publicSlug: "chasing-better-journey",
    status: TRACKER_STATUS.LIVE,
    createSetup: demoCreateSetup({
      durationWeeks: 12,
      checkInWeekdays: [1, 4],
      coachReviewMode: "every_n_weeks",
      coachReviewEveryWeeks: 4,
    }),
    programmes: [{ id: "prog_chasing_better", title: "Chasing Better" }],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_cb_coach", "q_assigned_coach", { required: false }),
      q("shared_cb_gym", "q_preferred_gym", { required: false }),
    ],
    tags: ["Goal Achieved", "Needs extra support", "On track", "At risk"],
    statuses: [
      status("st_cb_enrolled", "Enrolled", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_cb_progress", "In Progress", DEFAULT_STATUS_COLORS[1]),
      status("st_cb_review", "Review Due", "#7c3aed"),
      status(
        "st_cb_complete",
        "Journey Completed",
        DEFAULT_STATUS_COLORS[2],
        { isFinish: true }
      ),
      status("st_cb_dropped", "Dropped Out", DEFAULT_STATUS_COLORS[3], {
        isFinish: true,
      }),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: [
        {
          id: scheduleId("rel", "cb30"),
          label: "30 Day Review",
          amount: 30,
          unit: "days",
        },
        {
          id: scheduleId("rel", "cb60"),
          label: "60 Day Review",
          amount: 60,
          unit: "days",
        },
        {
          id: scheduleId("rel", "cb90"),
          label: "90 Day Review",
          amount: 90,
          unit: "days",
        },
      ],
      calendarDates: [],
      customUnit: "days",
      customPoints: [],
    },
    forms: [
      form("form_cb_signup", {
        name: "Sign-up & Baseline",
        role: "first_submission",
        shortDescription: "Goals, fitness level and starting measures.",
        nextFormId: "form_cb_30",
        unlockType: "after_submit",
        questions: [
          q("tq_cb_goal", "q_primary_goal", { required: true }),
          q("tq_cb_fitness", "q_current_fitness", { required: true }),
          q("tq_cb_start_w", "q_starting_weight", { required: true }),
          q("tq_cb_height", "q_height", { required: false }),
          q("tq_cb_medical", "q_medical_notes", { required: true }),
          q("tq_cb_photo", "q_progress_photo", { required: false }),
          q("tq_cb_programme", "q_programme", { required: false }),
        ],
      }),
      form("form_cb_30", {
        name: "30 Day Review",
        role: "follow_up",
        shortDescription: "First progress check-in.",
        nextFormId: "form_cb_60",
        unlockType: "after_days",
        unlockDays: 30,
        questions: [
          q("tq_cb30_weight", "q_current_weight", { required: true }),
          q("tq_cb30_feel", "q_how_feeling", { required: true }),
          q("tq_cb30_progress", "q_goal_progress", { required: true }),
          q("tq_cb30_energy", "q_energy_level", { required: false }),
          q("tq_cb30_nutrition", "q_nutrition_adherence", { required: false }),
          q("tq_cb30_wins", "q_wins_this_period", { required: false }),
          q("tq_cb30_photo", "q_progress_photo", { required: false }),
          q("tq_cb30_notes", "q_member_notes", { required: false }),
        ],
      }),
      form("form_cb_60", {
        name: "60 Day Review",
        role: "follow_up",
        shortDescription: "Mid-journey review.",
        nextFormId: "form_cb_90",
        unlockType: "after_days",
        unlockDays: 60,
        questions: [
          q("tq_cb60_weight", "q_current_weight", { required: true }),
          q("tq_cb60_bodyfat", "q_body_fat", { required: false }),
          q("tq_cb60_feel", "q_how_feeling", { required: true }),
          q("tq_cb60_progress", "q_goal_progress", { required: true }),
          q("tq_cb60_sessions", "q_sessions_attended", { required: false }),
          q("tq_cb60_blockers", "q_blockers", { required: false }),
          q("tq_cb60_focus", "q_next_focus", { required: true }),
          q("tq_cb60_photo", "q_progress_photo", { required: false }),
        ],
      }),
      form("form_cb_90", {
        name: "90 Day Review",
        role: "final",
        shortDescription: "Final checkpoint for this cycle.",
        nextFormId: "end",
        unlockType: "after_days",
        unlockDays: 90,
        questions: [
          q("tq_cb90_weight", "q_current_weight", { required: true }),
          q("tq_cb90_target", "q_target_weight", { required: false }),
          q("tq_cb90_attend", "q_attendance_percentage", { required: true }),
          q("tq_cb90_goal", "q_goal_achieved", { required: true }),
          q("tq_cb90_milestone", "q_milestone", { required: false }),
          q("tq_cb90_progress", "q_goal_progress", { required: true }),
          q("tq_cb90_photo", "q_progress_photo", { required: false }),
          q("tq_cb90_notes", "q_member_notes", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_each_submission",
      dueAfterDays: 0,
      overdueAfterDays: 3,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: false,
            helpText: "Notes from completing this review.",
          },
          0
        ),
        createCoachReviewQuestion(
          {
            label: "Action agreed with member",
            fieldType: "short_text",
            required: false,
          },
          1
        ),
        createCoachReviewQuestion(
          {
            label: "Needs extra support?",
            fieldType: "yes_no",
            required: true,
          },
          2
        ),
      ],
    },
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage:
        "Thanks — you are on the Chasing Better Journey. A coach will pick this up shortly.",
      confirmationEmailEnabled: true,
      confirmationEmailTemplate:
        "Hi {{first_name}},\n\nWelcome to {{journey_name}}. Your primary goal ({{primary_goal}}) is saved. We will check in at Day 30.\n\n— {{coach_name}}",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: { enabled: false, headline: "", showTop: 10, eligibleStatusIds: [] },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_chasing_better",
      enabled: true,
      recipients: "both",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_cb_welcome", "wf_cb_review_due", "wf_cb_day7"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: [
          "new_submission",
          "submission_updated",
          "review_due",
          "review_overdue",
          "follow_up_outstanding",
          "status_changed",
          "milestone_achieved",
        ].includes(item.id),
      })),
    },
    coaches: [DEMO_COACHES[0], DEMO_COACHES[1]],
    createdAt: ts,
    updatedAt: ts,
  });

  const quarterly = ensureTrackerShape({
    id: "tracker_quarterly_review",
    name: "Quarterly Member Review",
    description:
      "Fixed calendar review cycle (1 Jan / 1 Apr / 1 Jul / 1 Oct). Mid-cycle joiners enter the next quarter.",
    visitorMessage:
      "Your quarterly review keeps goals and attendance commitments on track.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.QUARTERLY_REVIEW,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_people_start",
    questionPattern: "different_forms_in_order",
    publicSlug: "quarterly-member-review",
    status: TRACKER_STATUS.LIVE,
    createSetup: demoCreateSetup({
      durationWeeks: "ongoing",
      coachReviewMode: "quarterly",
      checkInMode: "custom",
      checkInEveryDays: 90,
    }),
    programmes: [{ id: "prog_member_care", title: "Member Care" }],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_qr_coach", "q_assigned_coach", { required: false }),
      q("shared_qr_gym", "q_preferred_gym", { required: false }),
    ],
    tags: ["Fully Achieved", "Partially Achieved", "Needs reset"],
    statuses: [
      status("st_qr_active", "Active", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_qr_awaiting", "Awaiting Member Submission", "#7c3aed"),
      status("st_qr_reviewed", "Reviewed", DEFAULT_STATUS_COLORS[1]),
      status("st_qr_complete", "Quarter Complete", DEFAULT_STATUS_COLORS[2], {
        isFinish: false,
      }),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.CALENDAR,
      relativeOffsets: [],
      calendarDates: [
        { id: scheduleId("cal", "q1"), label: "Q1 Review", month: 1, day: 1 },
        { id: scheduleId("cal", "q2"), label: "Q2 Review", month: 4, day: 1 },
        { id: scheduleId("cal", "q3"), label: "Q3 Review", month: 7, day: 1 },
        { id: scheduleId("cal", "q4"), label: "Q4 Review", month: 10, day: 1 },
      ],
      customUnit: "days",
      customPoints: [],
    },
    forms: [
      form("form_qr_start", {
        name: "Quarter Start",
        role: "first_submission",
        shortDescription:
          "Set attendance commitment and quarterly goal (Update Existing pattern).",
        nextFormId: "form_qr_checkin",
        unlockType: "after_submit",
        questions: [
          q("tq_qr_commit", "q_attendance_commitment", { required: true }),
          q("tq_qr_goal", "q_quarterly_goal", { required: true }),
          q("tq_qr_stage", "q_week_stage", { required: false }),
          q("tq_qr_notes", "q_member_notes", { required: false }),
        ],
      }),
      form("form_qr_checkin", {
        name: "Quarter Review Update",
        role: "follow_up",
        shortDescription:
          "Update existing entry — Attendance Honoured, Goal Achieved, Milestone.",
        nextFormId: "end",
        unlockType: "after_days",
        unlockDays: 90,
        questions: [
          q("tq_qr_honoured", "q_attendance_honoured", { required: true }),
          q("tq_qr_achieved", "q_goal_achieved", { required: true }),
          q("tq_qr_progress", "q_goal_progress", { required: true }),
          q("tq_qr_milestone", "q_milestone", { required: false }),
          q("tq_qr_attend_pct", "q_attendance_percentage", {
            required: false,
          }),
          q("tq_qr_wins", "q_wins_this_period", { required: false }),
          q("tq_qr_blockers", "q_blockers", { required: false }),
          q("tq_qr_next", "q_next_focus", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_follow_ups",
      dueAfterDays: 0,
      overdueAfterDays: 5,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: true,
          },
          0
        ),
        createCoachReviewQuestion(
          {
            label: "Recommend programme change?",
            fieldType: "yes_no",
            required: false,
          },
          1
        ),
      ],
    },
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage:
        "Quarterly review received. Your coach will follow up if needed.",
      confirmationEmailEnabled: false,
      confirmationEmailTemplate: "",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: { enabled: false, headline: "", showTop: 10, eligibleStatusIds: [] },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_quarterly",
      enabled: true,
      recipients: "both",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_qr_due", "wf_qr_overdue"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: [
          "new_submission",
          "submission_updated",
          "review_due",
          "review_overdue",
          "status_changed",
        ].includes(item.id),
      })),
    },
    coaches: [DEMO_COACHES[1]],
    createdAt: ts,
    updatedAt: ts,
  });

  const onboarding = ensureTrackerShape({
    id: "tracker_programme_onboarding",
    name: "Programme Onboarding Journey",
    description:
      "Coach-led onboarding after programme purchase — Week 1 baseline, Week 4 and Week 8 progress, then Programme Complete.",
    visitorMessage:
      "Your coach will guide onboarding. Complete each week when it unlocks.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.PROGRAMME_ONBOARDING,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_people_start",
    questionPattern: "different_forms_in_order",
    publicSlug: "programme-onboarding",
    status: TRACKER_STATUS.LIVE,
    createSetup: demoCreateSetup({
      durationWeeks: 8,
      coachReviewEveryWeeks: 4,
      checkInWeekdays: [1],
    }),
    programmes: [{ id: "prog_hyrox_complete", title: "HYROX Complete" }],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_po_coach", "q_assigned_coach", { required: true }),
      q("shared_po_programme", "q_programme", { required: true }),
      q("shared_po_gym", "q_preferred_gym", { required: false }),
    ],
    tags: ["Onboarding", "Week 4", "Week 8", "Programme Complete"],
    statuses: [
      status("st_po_onboard", "Onboarding", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_po_week4", "Week 4 Progress", DEFAULT_STATUS_COLORS[1]),
      status("st_po_week8", "Week 8 Progress", "#7c3aed"),
      status(
        "st_po_complete",
        "Programme Complete",
        DEFAULT_STATUS_COLORS[2],
        { isFinish: true }
      ),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.CUSTOM,
      relativeOffsets: [],
      calendarDates: [],
      customUnit: "weeks",
      customPoints: [
        { id: scheduleId("cus", "w1"), name: "Week 1", value: 1 },
        { id: scheduleId("cus", "w4"), name: "Week 4", value: 4 },
        { id: scheduleId("cus", "w8"), name: "Week 8", value: 8 },
      ],
    },
    forms: [
      form("form_po_week1", {
        name: "Week 1 — Onboarding",
        role: "first_submission",
        shortDescription:
          "Current fitness, goals, experience and medical questionnaire.",
        nextFormId: "form_po_week4",
        unlockType: "after_submit",
        questions: [
          q("tq_po_fitness", "q_current_fitness", { required: true }),
          q("tq_po_goals", "q_fitness_goal", { required: true }),
          q("tq_po_primary", "q_primary_goal", { required: true }),
          q("tq_po_exp", "q_experience", { required: true }),
          q("tq_po_medical", "q_medical_notes", { required: true }),
          q("tq_po_start_w", "q_starting_weight", { required: false }),
          q("tq_po_stage", "q_week_stage", {
            required: false,
            helpText: "Defaults to Week 1.",
          }),
        ],
      }),
      form("form_po_week4", {
        name: "Week 4 — Progress",
        role: "follow_up",
        shortDescription: "Coach or member updates the same Tracker Entry.",
        nextFormId: "form_po_week8",
        unlockType: "after_days",
        unlockDays: 28,
        questions: [
          q("tq_po4_weight", "q_current_weight", { required: false }),
          q("tq_po4_sessions", "q_sessions_attended", { required: true }),
          q("tq_po4_energy", "q_energy_level", { required: true }),
          q("tq_po4_feel", "q_how_feeling", { required: true }),
          q("tq_po4_progress", "q_goal_progress", { required: true }),
          q("tq_po4_blockers", "q_blockers", { required: false }),
          q("tq_po4_photo", "q_progress_photo", { required: false }),
          q("tq_po4_stage", "q_week_stage", { required: false }),
        ],
      }),
      form("form_po_week8", {
        name: "Week 8 — Progress",
        role: "final",
        shortDescription: "Final onboarding checkpoint before Programme Complete.",
        nextFormId: "end",
        unlockType: "after_days",
        unlockDays: 56,
        questions: [
          q("tq_po8_weight", "q_current_weight", { required: false }),
          q("tq_po8_complete", "q_completion_percentage", { required: true }),
          q("tq_po8_attend", "q_attendance_percentage", { required: true }),
          q("tq_po8_goal", "q_goal_achieved", { required: true }),
          q("tq_po8_milestone", "q_milestone", { required: false }),
          q("tq_po8_race", "q_race_time", { required: false }),
          q("tq_po8_notes", "q_member_notes", { required: false }),
          q("tq_po8_file", "q_supporting_file", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_each_submission",
      dueAfterDays: 1,
      overdueAfterDays: 3,
      allowOverrideNextReview: true,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: true,
          },
          0
        ),
        createCoachReviewQuestion(
          {
            label: "Ready to mark Programme Complete?",
            fieldType: "yes_no",
            required: false,
          },
          1
        ),
      ],
    },
    settings: {
      requirePassword: true,
      password: "hyrox2026",
      submissionLimit: "",
      confirmationMessage:
        "Onboarding started. Your coach will review Week 1 details.",
      confirmationEmailEnabled: true,
      confirmationEmailTemplate:
        "Hi {{first_name}},\n\nWelcome to {{journey_name}} ({{programme}}). Your coach {{coach_name}} will guide Week 1–8.\n\nPrimary goal: {{primary_goal}}",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: { enabled: false, headline: "", showTop: 10, eligibleStatusIds: [] },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_programme_onboarding",
      enabled: true,
      recipients: "both",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_po_welcome", "wf_po_week4"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: [
          "new_submission",
          "submission_updated",
          "review_due",
          "follow_up_outstanding",
          "follow_up_completed",
          "status_changed",
          "milestone_achieved",
        ].includes(item.id),
      })),
    },
    coaches: [DEMO_COACHES[0], DEMO_COACHES[2]],
    createdAt: ts,
    updatedAt: ts,
  });

  const coachCheckIn = ensureTrackerShape({
    id: "tracker_coach_checkin",
    name: "Coach Check-in Journey",
    description:
      "Repeating weekly check-ins — same questions each time (energy, sleep, blockers, next focus).",
    visitorMessage: "Quick weekly check-in for your coach.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.COACH_REVIEW,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_publish_date",
    publishDate: "2026-01-01",
    questionPattern: "same_questions_repeating",
    publicSlug: "coach-check-in",
    status: TRACKER_STATUS.DRAFT,
    createSetup: demoCreateSetup({
      durationWeeks: "ongoing",
      journeyStartsFrom: "from_publish_date",
      publishDate: "2026-01-01",
      checkInMode: "custom",
      checkInEveryDays: 7,
      coachReviewMode: "each_checkin",
    }),
    programmes: [],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_ci_coach", "q_assigned_coach", { required: true }),
    ],
    tags: ["Weekly", "Needs support"],
    statuses: [
      status("st_ci_active", "Active", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_ci_paused", "Paused", DEFAULT_STATUS_COLORS[3]),
      status("st_ci_done", "Completed", DEFAULT_STATUS_COLORS[2], {
        isFinish: true,
      }),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: [
        {
          id: scheduleId("rel", "ci7"),
          label: "Weekly check-in",
          amount: 7,
          unit: "days",
        },
      ],
      calendarDates: [],
      customUnit: "days",
      customPoints: [],
    },
    forms: [
      form("form_ci_checkin", {
        name: "Weekly check-in",
        role: "first_submission",
        shortDescription: "Same questions repeating each week.",
        nextFormId: "end",
        unlockType: "after_submit",
        questions: [
          q("tq_ci_energy", "q_energy_level", { required: true }),
          q("tq_ci_sleep", "q_sleep_quality", { required: true }),
          q("tq_ci_feel", "q_how_feeling", { required: true }),
          q("tq_ci_nutrition", "q_nutrition_adherence", { required: false }),
          q("tq_ci_blockers", "q_blockers", { required: false }),
          q("tq_ci_focus", "q_next_focus", { required: true }),
          q("tq_ci_sessions", "q_sessions_attended", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_follow_ups",
      dueAfterDays: 0,
      overdueAfterDays: 2,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: false,
          },
          0
        ),
      ],
    },
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage: "Check-in saved. Thanks!",
      confirmationEmailEnabled: false,
      confirmationEmailTemplate: "",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: { enabled: false, headline: "", showTop: 10, eligibleStatusIds: [] },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_coach_checkin",
      enabled: false,
      recipients: "coach",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_ci_reminder"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: ["review_due", "review_overdue"].includes(item.id),
      })),
    },
    coaches: [DEMO_COACHES[2]],
    createdAt: ts,
    updatedAt: ts,
  });

  const challenge = ensureTrackerShape({
    id: "tracker_challenge_summer",
    name: "6-Week Summer Challenge",
    description:
      "Challenge journey — entry, mid-point check-in, final score + video. Coach verifies results for the leaderboard.",
    visitorMessage:
      "Join the Summer Challenge. Submit your entry, check in mid-way, then upload your final score and video.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.CHALLENGE,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_publish_date",
    publishDate: "2026-06-01",
    questionPattern: "different_forms_in_order",
    publicSlug: "summer-challenge-2026",
    status: TRACKER_STATUS.LIVE,
    createSetup: demoCreateSetup({
      durationWeeks: 6,
      journeyStartsFrom: "from_publish_date",
      publishDate: "2026-06-01",
      checkInMode: "weekly",
      checkInWeekdays: [1],
      coachReviewMode: "each_checkin",
    }),
    programmes: [{ id: "prog_summer_challenge", title: "Summer Challenge 2026" }],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_ch_coach", "q_assigned_coach", { required: false }),
      q("shared_ch_gym", "q_preferred_gym", { required: false }),
    ],
    tags: ["Verified", "Needs video", "Leaderboard", "DNF"],
    statuses: [
      status("st_ch_entered", "Entered", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_ch_active", "In Challenge", DEFAULT_STATUS_COLORS[1]),
      status("st_ch_pending", "Awaiting verification", "#7c3aed"),
      status("st_ch_verified", "Verified", DEFAULT_STATUS_COLORS[2], {
        isFinish: true,
      }),
      status("st_ch_dnf", "Did not finish", DEFAULT_STATUS_COLORS[3], {
        isFinish: true,
      }),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: [
        {
          id: scheduleId("rel", "ch21"),
          label: "Mid-challenge check-in",
          amount: 21,
          unit: "days",
        },
        {
          id: scheduleId("rel", "ch42"),
          label: "Final result",
          amount: 42,
          unit: "days",
        },
      ],
      calendarDates: [],
      customUnit: "days",
      customPoints: [],
    },
    forms: [
      form("form_ch_entry", {
        name: "Challenge entry",
        role: "first_submission",
        shortDescription: "Baseline measures and starting score.",
        nextFormId: "form_ch_mid",
        unlockType: "after_submit",
        questions: [
          q("tq_ch_goal", "q_primary_goal", { required: true }),
          q("tq_ch_fitness", "q_current_fitness", { required: true }),
          q("tq_ch_start_w", "q_starting_weight", { required: true }),
          q("tq_ch_score0", "q_challenge_score", { required: true }),
          q("tq_ch_photo0", "q_challenge_photo", { required: false }),
          q("tq_ch_medical", "q_medical_notes", { required: true }),
        ],
      }),
      form("form_ch_mid", {
        name: "Week 3 check-in",
        role: "follow_up",
        shortDescription: "Mid-challenge progress.",
        nextFormId: "form_ch_final",
        unlockType: "after_days",
        unlockDays: 21,
        questions: [
          q("tq_ch_mid_w", "q_current_weight", { required: true }),
          q("tq_ch_mid_score", "q_challenge_score", { required: true }),
          q("tq_ch_mid_feel", "q_how_feeling", { required: true }),
          q("tq_ch_mid_photo", "q_challenge_photo", { required: false }),
          q("tq_ch_mid_blockers", "q_blockers", { required: false }),
        ],
      }),
      form("form_ch_final", {
        name: "Final result",
        role: "final",
        shortDescription: "Final score, video proof, coach verification.",
        nextFormId: "end",
        unlockType: "after_days",
        unlockDays: 42,
        questions: [
          q("tq_ch_final_score", "q_challenge_score", { required: true }),
          q("tq_ch_final_w", "q_current_weight", { required: true }),
          q("tq_ch_final_video", "q_challenge_video", { required: true }),
          q("tq_ch_final_photo", "q_challenge_photo", { required: false }),
          q("tq_ch_final_points", "q_total_points", { required: false }),
          q("tq_ch_final_goal", "q_goal_achieved", { required: true }),
          q("tq_ch_final_notes", "q_member_notes", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_each_submission",
      dueAfterDays: 0,
      overdueAfterDays: 2,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Result verified?",
            fieldType: "yes_no",
            required: true,
          },
          0
        ),
        createCoachReviewQuestion(
          {
            label: "Verified score",
            fieldType: "number",
            required: false,
          },
          1
        ),
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: false,
          },
          2
        ),
      ],
    },
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage:
        "You are in the Summer Challenge. Complete mid and final check-ins when they unlock.",
      confirmationEmailEnabled: true,
      confirmationEmailTemplate:
        "Hi {{first_name}},\n\nYou are entered in {{journey_name}}. Upload your final video at the end for coach verification.\n\n— {{coach_name}}",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: {
      enabled: true,
      headline: "Summer Challenge Leaderboard",
      showTop: 10,
      eligibleStatusIds: ["st_ch_verified"],
    },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_challenge_summer",
      enabled: true,
      recipients: "both",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_ch_welcome", "wf_ch_final"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: [
          "new_submission",
          "review_due",
          "milestone_achieved",
          "status_changed",
        ].includes(item.id),
      })),
    },
    coaches: [DEMO_COACHES[0], DEMO_COACHES[1], DEMO_COACHES[2]],
    createdAt: ts,
    updatedAt: ts,
  });

  const consultation = ensureTrackerShape({
    id: "tracker_consultation",
    name: "Free Consultation Request",
    description:
      "Consultation requests — member books a chat; coach records outcome and next step.",
    visitorMessage:
      "Request a free consultation. A coach will confirm a time and follow up after your chat.",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.CONSULTATION,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    journeyStartsFrom: "from_people_start",
    questionPattern: "different_forms_in_order",
    publicSlug: "free-consultation",
    status: TRACKER_STATUS.LIVE,
    createSetup: demoCreateSetup({
      durationWeeks: 2,
      customDurationDays: 14,
      checkInMode: "custom",
      checkInEveryDays: 3,
      coachReviewMode: "each_checkin",
    }),
    programmes: [],
    personalInfoQuestions: personal,
    sharedQuestions: [
      q("shared_cs_coach", "q_assigned_coach", { required: false }),
      q("shared_cs_gym", "q_preferred_gym", { required: false }),
    ],
    tags: ["Hot lead", "Booked", "No-show", "Joined programme"],
    statuses: [
      status("st_cs_requested", "Requested", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      status("st_cs_booked", "Booked", DEFAULT_STATUS_COLORS[1]),
      status("st_cs_completed", "Consultation done", "#7c3aed"),
      status("st_cs_joined", "Joined programme", DEFAULT_STATUS_COLORS[2], {
        isFinish: true,
      }),
      status("st_cs_closed", "Closed — no join", DEFAULT_STATUS_COLORS[3], {
        isFinish: true,
      }),
    ],
    submissionSchedule: {
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: [
        {
          id: scheduleId("rel", "cs3"),
          label: "Coach follow-up",
          amount: 3,
          unit: "days",
        },
      ],
      calendarDates: [],
      customUnit: "days",
      customPoints: [],
    },
    forms: [
      form("form_cs_request", {
        name: "Consultation request",
        role: "first_submission",
        shortDescription: "Why they want a chat and preferred times.",
        nextFormId: "form_cs_outcome",
        unlockType: "after_submit",
        questions: [
          q("tq_cs_reason", "q_consultation_reason", { required: true }),
          q("tq_cs_goal", "q_primary_goal", { required: true }),
          q("tq_cs_fitness", "q_current_fitness", { required: false }),
          q("tq_cs_times", "q_preferred_times", { required: true }),
          q("tq_cs_programme", "q_programme", { required: false }),
          q("tq_cs_medical", "q_medical_notes", { required: false }),
        ],
      }),
      form("form_cs_outcome", {
        name: "Consultation outcome",
        role: "final",
        shortDescription: "Coach records the chat outcome.",
        nextFormId: "end",
        unlockType: "after_days",
        unlockDays: 3,
        questions: [
          q("tq_cs_out_feel", "q_how_feeling", { required: false }),
          q("tq_cs_out_focus", "q_next_focus", { required: true }),
          q("tq_cs_out_notes", "q_member_notes", { required: false }),
          q("tq_cs_out_follow", "q_follow_up_date", { required: false }),
        ],
      }),
    ],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: true,
      trigger: "after_each_submission",
      dueAfterDays: 0,
      overdueAfterDays: 1,
      questions: [
        createCoachReviewQuestion(
          {
            label: "Consultation outcome",
            fieldType: "dropdown",
            required: true,
            options: [
              "Joined programme",
              "Needs more info",
              "Not ready",
              "No-show",
            ],
          },
          0
        ),
        createCoachReviewQuestion(
          {
            label: "Programme recommended",
            fieldType: "short_text",
            required: false,
          },
          1
        ),
        createCoachReviewQuestion(
          {
            label: "Coach notes",
            fieldType: "long_text",
            required: false,
          },
          2
        ),
      ],
    },
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage:
        "Thanks — a coach will confirm your consultation time shortly.",
      confirmationEmailEnabled: true,
      confirmationEmailTemplate:
        "Hi {{first_name}},\n\nWe received your consultation request for {{journey_name}}. A coach will be in touch to book a time.\n\n— GymRite",
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      excelExportEnabled: true,
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: { enabled: false, headline: "", showTop: 10, eligibleStatusIds: [] },
    gymTvViews: createDefaultGymTvViews(),
    automation: {
      automationId: "automation_consultation",
      enabled: true,
      recipients: "coach",
      notifyUnassignedCoach: true,
      workflowIds: ["wf_cs_new", "wf_cs_followup"],
      events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
        id: item.id,
        enabled: ["new_submission", "review_due", "review_overdue"].includes(
          item.id
        ),
      })),
    },
    coaches: [DEMO_COACHES[0], DEMO_COACHES[2]],
    createdAt: ts,
    updatedAt: ts,
  });

  return [
    chasingBetter,
    quarterly,
    onboarding,
    coachCheckIn,
    challenge,
    consultation,
  ];
};

const buildDemoAutomations = (trackers) => {
  const byId = Object.fromEntries(trackers.map((t) => [t.id, t]));

  const chasing = createEmptyAutomation(byId.tracker_chasing_better);
  chasing.id = "automation_chasing_better";
  chasing.enabled = true;
  chasing.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_cb_welcome",
      name: "Welcome after sign-up",
      triggerModel: "event",
      triggerId: "new_submission",
      action: "email",
      recipients: "person",
      subject: "Welcome to {{journey_name}}",
      message:
        "Hi {{first_name}},\n\nThanks for joining {{journey_name}}. Your primary goal is {{primary_goal}}.\n\nA coach will pick this up shortly.\n\n— {{coach_name}}",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_cb_review_due",
      name: "Review Due reminder",
      triggerModel: "event",
      triggerId: "review_due",
      action: "notify",
      recipients: "both",
      subject: "Review Due — {{journey_name}}",
      message:
        "Hi {{first_name}}, a review on {{journey_name}} is due. Status: {{status}}.",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_cb_day7",
      name: "Day 7 relative nudge",
      triggerModel: "relative",
      triggerId: "relative_offset",
      relativeAmount: 7,
      relativeUnit: "days",
      relativeFrom: "journey_start",
      action: "email",
      recipients: "person",
      subject: "How is week 1 going?",
      message:
        "Hi {{first_name}},\n\nYou are one week into {{journey_name}}. How are you progressing towards {{primary_goal}}?\n\n— {{coach_name}}",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  const quarterly = createEmptyAutomation(byId.tracker_quarterly_review);
  quarterly.id = "automation_quarterly";
  quarterly.enabled = true;
  quarterly.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_qr_due",
      name: "Quarterly Review Due",
      triggerModel: "event",
      triggerId: "review_due",
      action: "email",
      recipients: "both",
      subject: "Quarterly review due — {{journey_name}}",
      message:
        "Hi {{first_name}},\n\nYour quarterly review for {{journey_name}} is due on {{review_date}}.\n\nQuarterly goal: {{quarterly_goal}}",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_qr_overdue",
      name: "Quarterly Review Overdue",
      triggerModel: "event",
      triggerId: "review_overdue",
      action: "notify",
      recipients: "coach",
      subject: "Review Overdue",
      message:
        "{{coach_name}}, {{first_name}} has an overdue quarterly review on {{journey_name}}.",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  const onboarding = createEmptyAutomation(byId.tracker_programme_onboarding);
  onboarding.id = "automation_programme_onboarding";
  onboarding.enabled = true;
  onboarding.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_po_welcome",
      name: "Onboarding welcome",
      triggerModel: "event",
      triggerId: "new_submission",
      action: "email",
      recipients: "both",
      subject: "Onboarding started — {{journey_name}}",
      message:
        "Hi {{first_name}},\n\n{{coach_name}} has started your onboarding for {{journey_name}}.\n\nPrimary goal: {{primary_goal}}\n\nSee you at Week 4.",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_po_week4",
      name: "Week 4 relative reminder",
      triggerModel: "relative",
      triggerId: "relative_offset",
      relativeAmount: 4,
      relativeUnit: "weeks",
      relativeFrom: "journey_start",
      action: "email",
      recipients: "person",
      subject: "Week 4 progress check",
      message:
        "Hi {{first_name}},\n\nWeek 4 of {{journey_name}} is here. Update your progress toward {{primary_goal}}.\n\n— {{coach_name}}",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  const checkin = createEmptyAutomation(byId.tracker_coach_checkin);
  checkin.id = "automation_coach_checkin";
  checkin.enabled = false;
  checkin.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_ci_reminder",
      name: "Weekly check-in due",
      triggerModel: "event",
      triggerId: "review_due",
      action: "notify",
      recipients: "person",
      subject: "Weekly check-in due",
      message:
        "Hi {{first_name}}, your weekly check-in on {{journey_name}} is due.",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  const challenge = createEmptyAutomation(byId.tracker_challenge_summer);
  challenge.id = "automation_challenge_summer";
  challenge.enabled = true;
  challenge.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_ch_welcome",
      name: "Challenge welcome",
      triggerModel: "event",
      triggerId: "new_submission",
      action: "email",
      recipients: "person",
      subject: "You are in {{journey_name}}",
      message:
        "Hi {{first_name}},\n\nWelcome to {{journey_name}}. Complete your mid check-in and final score + video for coach verification.\n\n— {{coach_name}}",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_ch_final",
      name: "Final result due",
      triggerModel: "relative",
      triggerId: "relative_offset",
      relativeAmount: 42,
      relativeUnit: "days",
      relativeFrom: "journey_start",
      action: "email",
      recipients: "both",
      subject: "Final challenge result due",
      message:
        "Hi {{first_name}},\n\nYour final result for {{journey_name}} is due. Upload your score and video.\n\n— {{coach_name}}",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  const consultation = createEmptyAutomation(byId.tracker_consultation);
  consultation.id = "automation_consultation";
  consultation.enabled = true;
  consultation.recipients = "coach";
  consultation.workflows = [
    {
      ...createEmptyWorkflow(),
      id: "wf_cs_new",
      name: "New consultation request",
      triggerModel: "event",
      triggerId: "new_submission",
      action: "notify",
      recipients: "coach",
      subject: "New consultation — {{journey_name}}",
      message:
        "{{coach_name}}, {{first_name}} requested a consultation on {{journey_name}}.",
      status: "active",
    },
    {
      ...createEmptyWorkflow(),
      id: "wf_cs_followup",
      name: "Consultation follow-up due",
      triggerModel: "event",
      triggerId: "review_due",
      action: "notify",
      recipients: "coach",
      subject: "Consultation follow-up due",
      message:
        "{{coach_name}}, follow up with {{first_name}} on {{journey_name}}.",
      status: "active",
    },
  ].map((wf) => ({ ...wf, createdAt: nowIso(), updatedAt: nowIso() }));

  return [
    chasing,
    quarterly,
    onboarding,
    checkin,
    challenge,
    consultation,
  ].map(normalizeAutomation);
};

/** Persist demo journeys + matching questions + parent automations. */
export const seedDemoJourneyTrackers = ({ force = false } = {}) => {
  mergeMissingDefaultQuestions(getQuestions());

  let existing = [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    existing = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(existing)) existing = [];
  } catch {
    existing = [];
  }

  const hasCurrentDemo = Boolean(localStorage.getItem(DEMO_FLAG_KEY));
  if (!force && existing.length > 0 && hasCurrentDemo) {
    return existing.map((t) => ensureTrackerShape(t));
  }

  const demoTrackers = buildDemoJourneyTrackers();
  const demoIds = new Set(demoTrackers.map((t) => t.id));
  // Upgrade path: replace known demo ids, keep any custom trackers.
  const custom =
    !force && existing.length > 0
      ? existing.filter((t) => !demoIds.has(t.id))
      : [];
  const trackers = [...demoTrackers, ...custom];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trackers));
  localStorage.setItem(DEMO_FLAG_KEY, nowIso());
  try {
    localStorage.removeItem("gymrite_journey_demo_v1");
  } catch {
    /* ignore */
  }

  // Write automations without recursive tracker overwrite loops:
  // saveAutomation syncs tracker links — load fresh after write.
  const parents = buildDemoAutomations(demoTrackers);
  const automationKey = "gymrite_tracker_automations_v2";
  let existingAutomations = [];
  try {
    const rawAuto = localStorage.getItem(automationKey);
    existingAutomations = rawAuto ? JSON.parse(rawAuto) : [];
    if (!Array.isArray(existingAutomations)) existingAutomations = [];
  } catch {
    existingAutomations = [];
  }
  const parentIds = new Set(parents.map((p) => p.id));
  const customAutos = force
    ? []
    : existingAutomations.filter(
        (a) => !parentIds.has(a.id) && !demoIds.has(a.trackerId)
      );
  localStorage.setItem(
    automationKey,
    JSON.stringify([...parents, ...customAutos])
  );

  // Re-sync tracker automation links via ensure shape already has ids;
  // refresh tracker automation.enabled from parents.
  const synced = trackers.map((tracker) => {
    const parent = parents.find((p) => p.trackerId === tracker.id);
    if (!parent) return ensureTrackerShape(tracker);
    return ensureTrackerShape({
      ...tracker,
      automation: {
        ...(tracker.automation || {}),
        automationId: parent.id,
        enabled: parent.enabled,
        recipients: parent.recipients,
        notifyUnassignedCoach: parent.notifyUnassignedCoach,
        events: parent.events,
        workflowIds: (parent.workflows || [])
          .filter((wf) => wf.status === "active")
          .map((wf) => wf.id),
      },
    });
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(synced));

  return synced;
};

export const hasDemoJourneySeed = () =>
  Boolean(localStorage.getItem(DEMO_FLAG_KEY));

/** Replace all trackers with the demo set (and remount automations for those ids). */
export const reloadDemoJourneyTrackers = () =>
  seedDemoJourneyTrackers({ force: true });
